/**
  Author: Lokesh Arora - lokesh.arora@enabledata.com
  Purpose: Enhance table component
  Date: may 01, 2020
**/

import React from 'react';

import {
  makeStyles,
  Table as MaUTable,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  Checkbox,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Input,
  CircularProgress
} from '@material-ui/core';
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  useFilters,
} from 'react-table';
import PropTypes from 'prop-types';
import TableToolbar from './tableToolbar';
import TablePaginationActions from './tablePaginationActions';
import ServerSidePaginationActions from './serverSidePaginationActions';
import './baseStyles.css';

/** STYLES HERE * */
const useStyles = makeStyles(() => ({
  centerTxt: {
    textAlign: 'center'
  },
  sortLabelIcon: {
    position: 'absolute',
    marginTop: '5px',
  },
  leftPadding: {
    paddingLeft: '20px !important',
  },
  noDataStyle: {
    display: 'block',
    textAlign: 'center',
    padding: '5vh',
    fontWeight: 'bold'
  },
  verticalAlign: {
    verticalAlign: 'top'
  }
}));

// For checkbox selection
const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <Checkbox color="primary" ref={resolvedRef} {...rest} />
      </>
    );
  },
);

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  return (
    <Input
      style={{ width: '100%' }}
      value={filterValue || ''}
      onChange={e => {
        setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
      }}
      placeholder={`Search`}
    />
  )
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val

function dateBetweenFilterFn(rows, id, filterValues) {
  const sd = filterValues[0] ? new Date(filterValues[0]).setHours(0, 0, 0, 0) : undefined
  const ed = filterValues[1] ? new Date(filterValues[1]).setHours(0, 0, 0, 0) : undefined

  if (ed || sd) {
    return rows.filter(r => {
      let cellDate = new Date(r.values[id])
      cellDate = cellDate.setHours(0, 0, 0, 0);
      if (ed && sd) {
        return cellDate >= sd && cellDate <= ed
      } else if (sd) {
        return cellDate >= sd
      } else if (ed) {
        return cellDate <= ed
      }
    })
  } else {
    return rows
  }
}


dateBetweenFilterFn.autoRemove = val => {
  return !val;
}


const EnhancedTable = ({
  paginationDefauldSize,
  columns,
  data,
  rowSelection,
  skipPageReset,
  toolbar,
  sortBy,
  loader,
  paging,
  paginationMode,
  ...rest
}) => {
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  )

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    }),
    []
  )

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    gotoPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState: {
        hiddenColumns: columns
          .filter((column) => column.show === false)
          .map((column) => column.accessor),
        sortBy: sortBy || [],
        pageSize: paginationDefauldSize ? paginationDefauldSize : 10,
      },
      autoResetPage: !skipPageReset,
      manualPagination: paginationMode === "server",
      pageCount: rest.pages,
      sortTypes: React.useMemo(
        () => ({
          caseInsensitive: (a, b, id, desc) => {
            try {
              const valueA = a.values[id] && a.values[id].toLowerCase();
              const valueB = b.values[id] && b.values[id].toLowerCase();
              return (valueA > valueB) ? 1 : ((valueA < valueB) ? -1 : 0)
              // return valueB > valueA ? 1 * factor : -1 * factor;
            } catch (e) {
              console.log('error ', e);
            }
          },
        }),
        [],
      ),
    },
    useFilters, // useFilters!
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.allColumns.push((cols) => {
        if (rowSelection) {
          return [
            // Let's make a column for selection
            {
              id: 'selection',
              // The header can use the table's getToggleAllRowsSelectedProps method
              // to render a checkbox.  Pagination is a problem since this will select all
              // rows even though not all rows are on the current page.  The solution should
              // be server side pagination.  For one, the clients should not download all
              // rows in most cases.  The client should only download data for the current page.
              // In that case, getToggleAllRowsSelectedProps works fine.
              Header: ({ getToggleAllRowsSelectedProps }) => (
                <div style={{ float: 'left' }}>
                  <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                </div>
              ),
              // The cell can use the individual row's getToggleRowSelectedProps method
              // to the render a checkbox
              Cell: ({ row }) => (
                <div>

                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                  />
                </div>
              ),
              width: 5,
              headerClass: true,
            },
            ...cols,
          ];
        }
        return [...cols];
      });
    },
  );

  // MAKE CLASS OBJECT FOR STYLES
  const classes = useStyles();
  // Handle Page No Change
  const handleChangePage = (event, newPage) => {
    gotoPage(newPage);
  };
  // Handle Page Size Change
  const handleChangeRowsPerPage = (event) => {
    setPageSize(Number(event.target.value));
  };

  // Render the UI for your table
  return (
    <TableContainer style={{ position: 'relative' }} className={rest?.className}>
      {loader && (
        <div style={{ width: '100%', textAlign: 'center', height: '100%', position: 'absolute', background: 'rgb(0 0 0 / 10%)', zIndex: '9999' }}>
          <p style={{ fontWeight: 'bold' }}>Please wait, data is being loaded...</p>
          <CircularProgress />
        </div>
      )}

      {toolbar && toolbar.show && (
        <TableToolbar
          customPadding={toolbar.customPadding}
          selected={selectedRowIds}
          preGlobalFilteredRows={preGlobalFilteredRows}
          setGlobalFilter={setGlobalFilter}
          globalFilter={globalFilter}
          gridTitle={toolbar.title}
          search={toolbar.enableSearch}
          actionButtonGroup={toolbar.actionButtonGroup}
        />
      )}
      <MaUTable {...getTableProps()} size="small">
        <TableHead>
          {headerGroups.map((headerGroup, rowIndex) => (
            <TableRow key={rowIndex} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, colIndex) => (

                <TableCell
                  className={
                    column.headerClass
                      ? `${classes.centerTxt} ${classes.verticalAlign}`
                      : column.procedureClass
                        ? ` ${classes.leftPadding} ${classes.verticalAlign}`
                        : classes.verticalAlign
                  }
                  key={colIndex}
                  width={column.width}
                  style={rest?.cellProps}
                >
                  <div {...(column.id === 'selection'
                    ? column.getHeaderProps()
                    : column.getHeaderProps(column.getSortByToggleProps()))}>
                    {column.render('Header')}
                    {column.id !== 'selection' ? (
                      <TableSortLabel
                        
                        className={`${classes.sortLabelIcon}`}
                        active={column.isSorted}
                        // react-table has a unsorted state which is not treated here
                        direction={column.isSortedDesc ? 'desc' : 'asc'}
                      />
                    ) : null}
                  </div>
                  
                  <div style={{ width: '90%', display: 'flex' }} data-testid={column.canFilter}>{column?.canFilter ? column.render('Filter') : null}</div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody className={rest.gridBodyStyle ? rest.gridBodyStyle : ''}>
          {(page.length > 0 && page.map((row, i) => {
            prepareRow(row);
            let classNames = "itiliti_tableRow";
            const rowProps = row.getRowProps();
            return (
              <React.Fragment key={rowProps.key}>
                <TableRow key={i} {...row.getRowProps()} className={classNames} >
                  {row.cells.map((cell, j) => {
                    return (
                      <TableCell
                        key={j}
                        {...cell.getCellProps()}
                        className={
                          cell.column.procedureClass ? classes.leftPadding : ''
                        }
                        style={rest?.cellProps}
                        onClick={(e) => {
                          if (rest?.handleRowClick) {
                            rest?.handleRowClick(e, row, cell, j)
                          }
                        }}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {row.isExpanded &&
                  rest?.renderRowSubComponent && rest?.renderRowSubComponent({ row, rowProps, visibleColumns })}
              </React.Fragment>
            );
          })) || <TableRow><TableCell colSpan={12}> <span className={classes.noDataStyle} style={rest?.noDataProps}>{rest?.noDataMessage ? rest?.noDataMessage : 'No Data Available'}</span></TableCell></TableRow>}
        </TableBody>
        <TableFooter>
          {paging == null && (
            <TableRow>
              {
                paginationMode === "server" ?
                  <TablePagination
                    // rowsPerPageOptions={[5, 10, 25, { label: 'All', value: data.length }]}
                    colSpan={12}
                    count={-1}
                    pages={rest.pages}
                    rowsPerPage={rest.limit}
                    page={rest.currentPage}
                    labelDisplayedRows={({ from, to, count }) => {
                      return `Page ${rest.currentPage} of  ${rest.pages} `;
                    }}
                    SelectProps={{
                      inputProps: { 'aria-label': 'rows per page' },
                      native: true,
                    }}
                    onPageChange={rest.handleChangePage}
                    onRowsPerPageChange={rest.handleChangeRowsPerPage}
                    ActionsComponent={(pageProps) => <ServerSidePaginationActions {...pageProps} pages={rest.pages} rowsPerPage={rest.limit}></ServerSidePaginationActions>}
                  /> : <TablePagination
                    // rowsPerPageOptions={[5, 10, 25, { label: 'All', value: data.length }]}
                    colSpan={12}
                    count={rows.length}
                    rowsPerPage={(paginationDefauldSize) ? paginationDefauldSize : pageSize}
                    page={pageIndex}
                    SelectProps={{
                      inputProps: { 'aria-label': 'rows per page' },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
              }
            </TableRow>
          )}
        </TableFooter>
      </MaUTable>
    </TableContainer>
  );
};

EnhancedTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  skipPageReset: PropTypes.bool.isRequired,
  toolbar: PropTypes.object,
};

export default React.memo(EnhancedTable);
